import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import DatePicker from 'react-datepicker';
import './services-table.scss';

// Portal the calendar to <body> so ancestor overflow:hidden / transform can't
// clip it (paired with popperProps strategy:'fixed').
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

// The row's date stays a `yyyy-mm-dd` string everywhere; convert only at the
// picker boundary.
const parseIso = (s?: string): Date | null => {
  if (!s) return null;
  // Build the Date at LOCAL NOON so the displayed day is correct in EVERY timezone
  // (midnight can shift to the previous/next day when the OS timezone offset is odd).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]), 12, 0, 0);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
import { PRICING_TYPE_OPTIONS, getArticleNumberByValue, getPricingTypeOption, getPricingTypeByArticleNumber } from './pricing-type-options.constants';
import { roundToQuarterHour } from '../utils/timeConversion.utils';
import { formatAmountWithLocale, sanitizePriceInput } from '../helper/common.helper';

interface TableRow {
  id: string;
  workingHourId?: number; // Preserved ID from edited invoice
  timeSheetWorkingHourId?: number; // Timesheet reference ID from timesheet data
  date: string;
  pricingType?: string; // New field for pricing type
  pricingTypeArticleNumber?: string; // Article number for API
  service: string;
  customWorkingDescription?: string; // Custom typed service description
  time: string;
  price: string;
  discount: { value: number; type: 'percentage' | 'fixed' };
  subtotal: number;
}

interface ServiceOption {
  value: string;
  label: string;
  id?: string;
}

interface ServicesTableProps {
  tableRows: TableRow[];
  serviceOptions: ServiceOption[];
  currency: string;
  isHourlyPrice: boolean;
  isDailyPrice: boolean;
  isWeeklyPrice: boolean;
  isMonthlyPrice: boolean;
  deductionType?: string;
  t: (key: string) => string;
  onRowChange: (rowId: string, field: string, value: any) => void;
  onRemoveRow: (rowId: string) => void;
  onAddRow?: (rowData: TableRow) => void;
  globalData?: any;
  preferences?: {
    vat?: number; // Dynamic VAT from invoice preferences
  };
  onServiceTableDataChange?: (data: {
    totalAmount: number;
    hourlyWagesAmount: number;
    workingHours: Array<{
      id: number;
      workingDate: string;
      dateId: string;
      number: number;
      unit: string;
      vat: number;
      isArticleShownInInvoice: boolean;
      amountPerUnit: number;
      amount: number;
      articleNumber: string;
      workingDescription: string;
      houseworkType: string;
      workingHour: number;
      CustomWorkingDescription: string;
      isUnitArticle: boolean;
    }>;
  }) => void;
  validateServiceDescriptions?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  validateDates?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  freelancerId?: string;
  timesheetData?: any[];  // Timesheet data from Redux
  customerState?: any;  // Customer state for blink logic
}

const ServicesTable: React.FC<ServicesTableProps> = ({
  tableRows,
  serviceOptions,
  currency,
  isHourlyPrice,
  isDailyPrice,
  isWeeklyPrice,
  isMonthlyPrice,
  deductionType,
  t: propT,
  onRowChange,
  onRemoveRow,
  onAddRow,
  globalData,
  preferences,
  onServiceTableDataChange,
  validateServiceDescriptions,
  validateDates,
  freelancerId = 'default',
  timesheetData,
  customerState
}) => {
  // Use translation hook
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // Ref to track if timesheet data has been processed - prevents duplicate processing
  const timesheetProcessedRef = useRef(false);
  
  // Track previous deduction type to clear services when it changes
  const prevDeductionTypeRef = useRef<string | undefined>(deductionType);
  
  // Check if Generate Row button should blink (customer selected, no working hours)
  const shouldButtonBlink = useMemo(() => {
    const hasCustomer = customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id;
    const hasWorkingHours = tableRows.length > 0;
    return hasCustomer && !hasWorkingHours;
  }, [customerState?.selectedCustomer, tableRows]);
  // State for custom dropdown per row
  const [openDropdownRowId, setOpenDropdownRowId] = useState<string | null>(null);
  const dropdownRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  // State for pricing type dropdown per row
  const [openPricingTypeRowId, setOpenPricingTypeRowId] = useState<string | null>(null);
  const pricingTypeRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  // State for service dropdown per row (for non-RUT/ROT services)
  const [openServiceDropdownRowId, setOpenServiceDropdownRowId] = useState<string | null>(null);
  const serviceDropdownRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  // Refs for portaled dropdown list elements (needed for click-outside detection)
  const servicePortalListRef = useRef<{[key: string]: HTMLDivElement | null}>({});
  const pricingPortalListRef = useRef<{[key: string]: HTMLDivElement | null}>({});

  // State to track dropdown positions (for fixed positioning outside table)
  const [dropdownPositions, setDropdownPositions] = useState<{[key: string]: {top: number, left: number, width: number}}>({}); 

  // Helper function to calculate dropdown position for fixed positioning
  const calculateDropdownPosition = (triggerElement: HTMLDivElement | null) => {
    if (!triggerElement) return { top: 0, left: 0, width: 250 };
    
    const rect = triggerElement.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    };
  };

  // Update dropdown position when opened or on scroll
  useEffect(() => {
    const updateDropdownPositions = () => {
      const newPositions: {[key: string]: {top: number, left: number, width: number}} = {};
      
      if (openPricingTypeRowId && pricingTypeRefs.current[openPricingTypeRowId]) {
        newPositions[openPricingTypeRowId] = calculateDropdownPosition(pricingTypeRefs.current[openPricingTypeRowId]);
      }
      
      if (openDropdownRowId && dropdownRefs.current[openDropdownRowId]) {
        newPositions[openDropdownRowId] = calculateDropdownPosition(dropdownRefs.current[openDropdownRowId]);
      }
      
      setDropdownPositions(newPositions);
    };
    
    // Update positions when dropdowns open
    updateDropdownPositions();
    
    // Update positions on scroll and resize
    window.addEventListener('scroll', updateDropdownPositions, true);
    window.addEventListener('resize', updateDropdownPositions);
    
    return () => {
      window.removeEventListener('scroll', updateDropdownPositions, true);
      window.removeEventListener('resize', updateDropdownPositions);
    };
  }, [openPricingTypeRowId, openDropdownRowId]);

  // State to store all service data
  const [serviceData, setServiceData] = useState<{[key: string]: TableRow}>({});

  // State to track validation errors for each row (for tooltip display)
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  
  // Ref to store timeout IDs for auto-hiding validation errors
  const validationTimeoutRefs = useRef<{[key: string]: NodeJS.Timeout}>({});
  
  // State to track which rows should blink
  const [blinkingRows, setBlinkingRows] = useState<Set<string>>(new Set());

  // State to track which rows have missing date
  const [dateErrorRows, setDateErrorRows] = useState<Set<string>>(new Set());
  const dateInputRefs = useRef<{[key: string]: HTMLElement | null}>({});

  // Display-only: remembers the exact separator the user typed in the time field (',' '.' or ':').
  // row.time itself is always kept in canonical HH:MM (colon) form so calculations/payload are unaffected.
  const [rawTimeInputs, setRawTimeInputs] = useState<{[rowId: string]: string}>({});

  // Which row's minutes dropdown is open (only one at a time). The menu is
  // portalled to <body> so it can never be covered/clipped by later rows.
  const [openMinutesRowId, setOpenMinutesRowId] = useState<string | null>(null);
  const [minutesMenuPos, setMinutesMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const minutesDropdownRef = useRef<HTMLDivElement>(null);
  const minutesMenuRef = useRef<HTMLDivElement>(null);
  const openMinutesDropdown = (rowId: string, triggerEl: HTMLElement) => {
    const r = triggerEl.getBoundingClientRect();
    setMinutesMenuPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 76) });
    setOpenMinutesRowId(rowId);
  };
  useEffect(() => {
    if (!openMinutesRowId) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        (minutesDropdownRef.current && minutesDropdownRef.current.contains(t)) ||
        (minutesMenuRef.current && minutesMenuRef.current.contains(t))
      ) return;
      setOpenMinutesRowId(null);
    };
    // A fixed-position menu would detach on scroll — just close it instead.
    const onScrollOrResize = () => setOpenMinutesRowId(null);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openMinutesRowId]);

  // ── Seamless handoff between the row list's scroll and the page scroll ───────
  // The rows sit in their own capped-height scroller. Browsers "latch" a wheel
  // gesture to whichever scroller it began on, so when the list reaches its end the
  // page only takes over once you stop and scroll again — which reads as a freeze.
  // Forward the leftover delta to the page ourselves, eased over a few frames so
  // the two scrollers feel like one continuous motion.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    // The page may scroll on <body> or on a layout container — find whichever.
    const findPageScroller = (): HTMLElement | null => {
      let p: HTMLElement | null = el.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
        p = p.parentElement;
      }
      return null; // null → scroll the window
    };

    let pending = 0;
    let raf = 0;
    let target: HTMLElement | null = null;

    const applyScroll = (px: number) => {
      if (target) target.scrollTop += px;
      else window.scrollBy(0, px);
    };

    const flush = () => {
      if (Math.abs(pending) < 1) {
        applyScroll(pending);
        pending = 0;
        raf = 0;
        return;
      }
      const move = pending * 0.35; // ease out; converges in ~3 frames for a trackpad
      pending -= move;
      applyScroll(move);
      raf = requestAnimationFrame(flush);
    };

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // horizontal gesture
      if (el.scrollHeight <= el.clientHeight) return;       // not a scroller (mobile) → native chaining is fine

      const atTop = el.scrollTop <= 0;
      const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
      const goingDown = e.deltaY > 0;
      if (!((goingDown && atBottom) || (!goingDown && atTop))) return; // list still has room

      // Deltas arrive in pixels, lines (Firefox) or pages — normalise to pixels.
      const delta =
        e.deltaMode === 1 ? e.deltaY * 16 :
        e.deltaMode === 2 ? e.deltaY * el.clientHeight :
        e.deltaY;

      target = findPageScroller();
      pending += delta;
      e.preventDefault();
      if (!raf) raf = requestAnimationFrame(flush);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Ref to store input elements for scrolling
  const inputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});

  // Track previous data to prevent infinite loops
  const prevDataRef = useRef<string>('');
  
  // Track normalized row IDs to prevent infinite loops
  const normalizedRowsRef = useRef<Set<string>>(new Set());
  
  // Debounce timer for custom service description changes
  const customServiceDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check if deduction type is RUT or ROT (needed early for useEffect dependencies)
  const isRutOrRotDeduction = deductionType === 'rut' || deductionType === 'rot';

  // Helper function to convert time string (hh:mm) to decimal hours
  const timeToHours = (timeStr: string): number => {
    if (!timeStr || timeStr.trim() === '') return 0;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return 0;
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours + (minutes / 60);
  };

  // Helper function to convert decimal hours to hh:mm format
  // Example: 8.5 hours = 08:30 (8 hours + 0.5 * 60 = 30 minutes)
  const hoursToTime = (decimalHours: number): string => {
    if (!decimalHours || decimalHours <= 0) return '08:00';
    
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    
    // Format with leading zeros
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Helper function to normalize incorrectly formatted time strings
  // Converts: 08.50 -> 08:30, 08.75 -> 08:45, etc.
  // This handles cases where decimal point is used instead of colon,
  // and the decimal part represents hours (not minutes)
  const normalizeTimeString = (timeStr: string): string => {
    if (!timeStr || timeStr.trim() === '') return timeStr;
    
    // Check if string contains decimal point (08.50, 08.75, etc.)
    if (timeStr.includes('.') && !timeStr.includes(':')) {
      const parts = timeStr.split('.');
      const hours = parseInt(parts[0] || '0');
      const decimal = parseInt(parts[1] || '0');
      
      if (decimal <= 59) {
        // User means HH.MM format (5.30 = 5 hours 30 minutes)
        return `${hours.toString().padStart(2, '0')}:${decimal.toString().padStart(2, '0')}`;
      } else {
        // Treat as decimal hours (5.75 = 5.75 hours = 5:45)
        const decimalValue = parseFloat(timeStr);
        if (!isNaN(decimalValue) && decimalValue > 0) {
          return hoursToTime(decimalValue);
        }
      }
    }
    
    // Return original string if no conversion needed
    return timeStr;
  };

  // Helper function to convert date from MM/DD/YYYY to YYYY/MM/DD format
  const formatDateForAPI = (dateStr: string): string => {
    if (!dateStr) return '';
    
    // Check if date is already in YYYY-MM-DD format (keep dashes)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr; // Return as-is with dashes
    }
    
    // Check if date is in YYYY/MM/DD format (convert slashes to dashes)
    if (dateStr.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
      return dateStr.replace(/\//g, '-'); // Convert slashes to dashes
    }
    
    // Handle MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Convert to YYYY-MM-DD format
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return dateStr; // Return as-is if format not recognized
  };

  // Map timesheet data to table rows when timesheet=true in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const timesheetParam = searchParams.get('timesheet');
    
    
    // Only map timesheet data if:
    // 1. timesheet=true in URL (we're in timesheet mode)
    // 2. timesheetData exists and has items
    // 3. tableRows is empty (no existing data to override)
    // 4. timesheetData has not been processed yet (prevent duplicates)
    if (timesheetParam === 'true' && timesheetData && timesheetData.length > 0 && tableRows.length === 0 && !timesheetProcessedRef.current) {
      // Mark as processed immediately to prevent re-entry
      timesheetProcessedRef.current = true;
      
      // Track processed timeSheetWorkingHourIds to prevent duplicates within this batch
      const processedIds = new Set<number>();
      
      // Tax-free article numbers that should be filtered out when timesheet=true
      const VALID_PRICING_ARTICLE_NUMBERS = ['92', '94', '95', '96', '97'];
      
      timesheetData.forEach((item: any, index: number) => {
        // Skip if this timeSheetWorkingHourId was already processed
        const timesheetId = item.timeSheetWorkingHourId || item.id || 0;
        if (timesheetId !== 0 && processedIds.has(timesheetId)) {
          return;
        }
        
        // Only show standard pricing-type articles (92, 94, 95, 96, 97).
        // Any other articleNumber (tax-free, expense, mileage, allowance, etc.) is skipped.
        const articleNumber = item.articleNumber?.toString() || '';
        if (!VALID_PRICING_ARTICLE_NUMBERS.includes(articleNumber)) {
          return; // Skip this row - not a standard working-hour article
        }
        
        // Check if row with this timeSheetWorkingHourId already exists in tableRows
        const alreadyExists = tableRows.some((row: TableRow) => 
          row.timeSheetWorkingHourId && 
          row.timeSheetWorkingHourId === timesheetId && 
          timesheetId !== 0
        );
        
        if (alreadyExists) {
          return;
        }
        
        // Mark this ID as processed
        if (timesheetId !== 0) {
          processedIds.add(timesheetId);
        }
        
        // Format date from "2026-01-09T00:00:00" to "01/09/2026"
        let formattedDate = '';
        if (item.workingDate) {
          const datePart = item.workingDate.split('T')[0]; // Get "2026-01-09"
          const [year, month, day] = datePart.split('-');
          formattedDate = `${month}/${day}/${year}`; // Convert to MM/DD/YYYY
        }
        
        // Get pricing type by article number
        // If isUnitArticle is true, force 'st' (Payment Per Unit) since it shares article 97 with 'a'
        const pricingTypeOption = item.isUnitArticle === true
          ? { value: 'st', articleNumber: '97' }
          : getPricingTypeByArticleNumber(item.articleNumber);
        
        const isUnitPricingType = pricingTypeOption?.value === 'st';
        
        // Format time from timesheet
        // Payment Per Unit: use integer number (quantity); Hourly: convert to HH.MM; others: '01.00'
        let formattedTime = '08.00';
        if (isUnitPricingType) {
          // Payment Per Unit: quantity is stored in item.number
          const qty = Math.round(item.number || 1);
          formattedTime = String(qty > 0 ? qty : 1);
        } else if (item.workingHour !== undefined && item.workingHour !== null) {
          const hourValue = parseFloat(item.workingHour.toString());
          if (!isNaN(hourValue)) {
            // Split into hours and decimal part
            const hours = Math.floor(hourValue);
            const decimalPart = hourValue - hours;
            // Convert decimal to minutes (0.5 = 30 minutes, 0.25 = 15 minutes)
            const minutes = Math.round(decimalPart * 60);
            // Format as HH.MM
            formattedTime = `${hours.toString().padStart(2, '0')}.${minutes.toString().padStart(2, '0')}`;
          }
        }
        
        const newRow: TableRow = {
          id: uuidv4(),
          workingHourId: item.id || 0,
          timeSheetWorkingHourId: timesheetId,
          date: formattedDate,
          pricingType: pricingTypeOption?.value || 'Tim',
          pricingTypeArticleNumber: item.articleNumber || '92',
          service: '',
          customWorkingDescription: item.customWorkingDescription || '',
          time: formattedTime,
          price: item.amountPerUnit?.toString() || '0',
          discount: { value: 0, type: 'percentage' as const },
          subtotal: item.amount || 0
        };
        
        if (onAddRow) {
          onAddRow(newRow);
        }
      });
      
    }
  }, [timesheetData, location.search, tableRows.length, onAddRow]);
  
  // Log tableRows whenever they change
  useEffect(() => {
    // TableRows updated
  }, [tableRows]);

  // Helper function to calculate subtotal
  const calculateSubtotal = (row: TableRow): number => {
    // Parse price, if empty or invalid, use 0 for calculation
    const pricePerUnit = row.price && row.price !== '' ? parseFloat(row.price) : 0;
    // Determine units based on pricing type
    let units = 1; // Default to 1 unit
    
    // Check if pricing type is "Tim" (hourly)
    const isTimPricingType = row.pricingType === 'Tim' || !row.pricingType;
    
    if (isTimPricingType) {
      // For hourly pricing: check if time is already a decimal number or needs conversion
      const timeValue = row.time && String(row.time).trim() !== '' ? row.time : '08:00';
      
      // Check if it's already a decimal number (from timesheet: "8.5")
      const isDecimalNumber = !timeValue.includes(':') && /^\d+\.?\d*$/.test(timeValue);
      
      if (isDecimalNumber) {
        // Direct numeric value from timesheet (e.g., "8.5" hours)
        units = parseFloat(timeValue) || 8;
      } else {
        // Time string that needs normalization and conversion (e.g., "08:30", "08.50")
        const normalizedTime = normalizeTimeString(timeValue);
        units = timeToHours(normalizedTime);
      }
    } else {
      // For other pricing types (Dag, vecka, m, a): use time value as units directly
      // Default to 1 unit if time is empty or invalid
      const timeValue = row.time && String(row.time).trim() !== '' ? row.time : '1';
      units = parseFloat(timeValue) || 1;
    }
    
    // Calculate amount without discount
    const amountWithoutDiscount = pricePerUnit * units;
    
    if (!row.discount || row.discount.value === 0) {
      return amountWithoutDiscount;
    }
    
    let discountAmount = 0;
    
    if (row.discount.type === 'percentage') {
      // For percentage: (amount without discount * discount percentage) / 100
      discountAmount = (amountWithoutDiscount * row.discount.value) / 100;
    } else {
      // For fixed currency
      discountAmount = row.discount.value;
    }
    
    // Amount after discount = amount without discount - discount amount
    const amountAfterDiscount = amountWithoutDiscount - discountAmount;
    
    return Math.max(0, amountAfterDiscount); // Ensure non-negative
  };

  // Normalize time values on component mount and when tableRows change
  useEffect(() => {
    tableRows.forEach(row => {
      // Skip if already normalized this row with this time value
      const rowKey = `${row.id}-${row.time}`;
      if (normalizedRowsRef.current.has(rowKey)) {
        return;
      }
      
      if (row.time && row.time.trim() !== '' && (row.pricingType === 'Tim' || !row.pricingType)) {
        const normalizedTime = normalizeTimeString(row.time);
        // If time was normalized (changed), update it
        if (normalizedTime !== row.time) {
          // Mark this row as normalized
          normalizedRowsRef.current.add(rowKey);
          normalizedRowsRef.current.add(`${row.id}-${normalizedTime}`);
          onRowChange(row.id, 'time', normalizedTime);
        } else {
          // Mark as already normalized (no change needed)
          normalizedRowsRef.current.add(rowKey);
        }
      }
    });
  }, [tableRows, onRowChange]);

  // Update serviceData and recalculate subtotal whenever tableRows change
  useEffect(() => {
    const newServiceData: {[key: string]: TableRow} = {};
    tableRows.forEach(row => {
      const subtotal = calculateSubtotal(row);
      newServiceData[row.id] = {
        ...row,
        subtotal
      };
    });
    setServiceData(newServiceData);
    // If tableRows is empty, immediately clear serviceData
  
  }, [tableRows, isHourlyPrice]);

  // Clear validation errors for removed rows
  useEffect(() => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      const currentRowIds = new Set(tableRows.map(row => row.id));
      
      // Remove errors for rows that no longer exist
      Object.keys(newErrors).forEach(rowId => {
        if (!currentRowIds.has(rowId)) {
          delete newErrors[rowId];
          // Clear timeout if exists
          if (validationTimeoutRefs.current[rowId]) {
            clearTimeout(validationTimeoutRefs.current[rowId]);
            delete validationTimeoutRefs.current[rowId];
          }
        }
      });
      
      return newErrors;
    });
  }, [tableRows]);

  // Clear services when deduction type changes between ROT and RUT
  useEffect(() => {
    // Check if deduction type has changed from one type to another (not just initial mount)
    if (prevDeductionTypeRef.current !== deductionType && 
        prevDeductionTypeRef.current !== undefined &&
        ((prevDeductionTypeRef.current === 'rot' && deductionType === 'rut') ||
         (prevDeductionTypeRef.current === 'rut' && deductionType === 'rot'))) {
      
      // Clear service selections for all rows that have pricing type 'Tim' or no pricing type
      tableRows.forEach(row => {
        const shouldClearService = (row.pricingType === 'Tim' || !row.pricingType) && row.service;
        if (shouldClearService) {
          onRowChange(row.id, 'service', '');
        }
      });
    }
    
    // Update previous deduction type
    prevDeductionTypeRef.current = deductionType;
  }, [deductionType, tableRows, onRowChange]);

  // Calculate and send data to parent component for API payload
  useEffect(() => {
    if (!onServiceTableDataChange) return;

    // Handle empty table rows - send empty data to clear parent state
    if (tableRows.length === 0) {
      const emptyDataStr = JSON.stringify({
        totalAmount: 0,
        hourlyWagesAmount: 0,
        workingHoursCount: 0,
        workingHoursData: []
      });
  // Always send empty data when tableRows is 0, regardless of prevDataRef
      // This ensures the last row removal triggers the API call
      if (prevDataRef.current !== emptyDataStr) {
        prevDataRef.current = emptyDataStr;

        // Send empty data to parent
        onServiceTableDataChange({
          totalAmount: 0,
          hourlyWagesAmount: 0,
          workingHours: []
        });
      } 
      return;
    }

    // Calculate total amount from subtotals
    const totalAmount = tableRows.reduce((sum, row) => {
      return sum + calculateSubtotal(row);
    }, 0);

    // Calculate HourlyWagesAmount (only for articleNumber 92 - Hourly Wages)
    const hourlyWagesAmount = tableRows
      .filter(row => row.pricingTypeArticleNumber === '92')
      .reduce((sum, row) => sum + calculateSubtotal(row), 0);

    // Prepare workingHours array
    const workingHours = tableRows.map(row => {
      
      // Get price - use row.price if provided, otherwise default 500
      const priceValue = row.price && String(row.price).trim() !== '' ? parseFloat(row.price) : 500;
      
      // Determine if this is hourly pricing (Tim) or other pricing type
      const isTimPricing = row.pricingType === 'Tim' || !row.pricingType;
      
      // Get time/units value based on pricing type
      const timeValue = row.time && String(row.time).trim() !== '' ? row.time : (isTimPricing ? '08:00' : '1');
      
      // For hourly pricing: Check if timeValue is already a decimal number (from timesheet: "8.5")
      // or a time string that needs normalization (user input: "08:30" or "08.50")
      let workingHourValue: number;
      if (isTimPricing) {
        // Check if it's already a decimal number (no colon, contains decimal point or is just a number)
        const isDecimalNumber = !timeValue.includes(':') && /^\d+\.?\d*$/.test(timeValue);
        if (isDecimalNumber) {
          // Direct numeric value from timesheet (e.g., "8.5" hours)
          workingHourValue = parseFloat(timeValue) || 8;
        } else {
          // Time string that needs normalization and conversion (e.g., "08:30", "08.50")
          const normalizedTimeValue = normalizeTimeString(timeValue);
          workingHourValue = timeToHours(normalizedTimeValue);
        }
      } else {
        // Non-hourly pricing: use numeric value directly
        workingHourValue = parseFloat(timeValue) || 1;
      }
      
      // Convert date to YYYY/MM/DD format for API
      const formattedDate = formatDateForAPI(row.date);
      
      // Get the dynamic housework type mapping
      const { mapping: currentHouseworkTypeMapping } = getRutRotServiceOptionsAndMapping();
      
      // Determine workingDescription and CustomWorkingDescription based on deduction type AND pricing type
      let workingDescriptionValue = '';
      let customWorkingDescriptionValue = '';
      let houseworkTypeValue = ''; // Add houseworkType value variable
      
      // Check if ROT/RUT dropdown should be used (same logic as UI rendering)
      const shouldUseRutRotDropdown = isRutOrRotDeduction && (row.pricingType === 'Tim' || !row.pricingType);
      
      if (shouldUseRutRotDropdown) {
        // ROT/RUT with Hourly (Tim) pricing: use dropdown service value
        // Find the service option and get its translated label
        const selectedService = rutServiceOptions.find(option => option.value === row.service);
        const translatedServiceValue = selectedService ? selectedService.label : (row.service || '');
        
        // Get the houseworkType constant from mapping
        const houseworkTypeConstant = currentHouseworkTypeMapping[row.service] || '';
        
        workingDescriptionValue = translatedServiceValue; // Translated label for display
        houseworkTypeValue = houseworkTypeConstant; // Uppercase constant for API
        customWorkingDescriptionValue = '';
      } else {
        // All other cases (non-ROT/RUT OR non-Hourly pricing): use custom typed text
        workingDescriptionValue = '';
        houseworkTypeValue = ''; // Empty for non-ROT/RUT cases
        customWorkingDescriptionValue = row.customWorkingDescription || '';
      }
    
     return {
        id: row.workingHourId || 0, // Use preserved ID from edited invoice, 0 for new rows
        workingDate: formattedDate,
        dateId: `${formattedDate}-${uuidv4()}`,
        number: workingHourValue, // Use calculated value based on pricing type
        unit: row.pricingType === 'st' ? 'a' : (row.pricingType || 'Tim'), // 'st' is internal-only; send 'a' for Payment Per Unit (isUnitArticle differentiates it)
        vat: preferences?.vat || globalData?.vat?.globalValue || 25,
        isArticleShownInInvoice: true,
        amountPerUnit: priceValue,
        amount: calculateSubtotal(row),
        articleNumber: row.pricingTypeArticleNumber || '92',
        workingDescription: workingDescriptionValue,
        houseworkType: houseworkTypeValue, // Uppercase constant (e.g., CONSTRUCTION, ELECTRICITY)
        workingHour: workingHourValue, // Will be 8 if time is empty/invalid
        CustomWorkingDescription: customWorkingDescriptionValue, // Custom typed text
        timeSheetWorkingHourId: row.timeSheetWorkingHourId || 0, // Timesheet reference ID, 0 if not from timesheet
        isUnitArticle: row.pricingType === 'st' // true only for Payment Per Unit
      };
    });
   

    // Create a stable string representation to detect actual changes
    // NOTE: dateId is excluded because it contains uuidv4() and changes on every render
    const currentDataStr = JSON.stringify({
      totalAmount,
      hourlyWagesAmount,
      workingHoursCount: workingHours.length,
      workingHoursData: workingHours.map(wh => ({
        workingDate: wh.workingDate,
        number: wh.number,
        amountPerUnit: wh.amountPerUnit,
        amount: wh.amount,
        workingDescription: wh.workingDescription,
        CustomWorkingDescription: wh.CustomWorkingDescription
      }))
    });
    
    // Check if only typed fields changed (price, time, custom description) for debouncing
    const prevData = prevDataRef.current ? JSON.parse(prevDataRef.current) : null;
    const currentData = JSON.parse(currentDataStr);
    
    let onlyTypedFieldsChanged = false;
    if (prevData && prevData.workingHoursCount === currentData.workingHoursCount) {
      // Structural changes: row count, date, or dropdown-driven workingDescription
      const hasStructuralChanges = currentData.workingHoursData.some((curr: any, index: number) => {
        const prev = prevData.workingHoursData[index];
        if (!prev) return true;
        return curr.workingDate !== prev.workingDate ||
               curr.workingDescription !== prev.workingDescription;
      });
      
      // Typed field changes: number (time), amountPerUnit (price), amount (subtotal), CustomWorkingDescription
      const hasTypedFieldChanges = currentData.workingHoursData.some((curr: any, index: number) => {
        const prev = prevData.workingHoursData[index];
        if (!prev) return true;
        return curr.number !== prev.number ||
               curr.amountPerUnit !== prev.amountPerUnit ||
               curr.amount !== prev.amount ||
               curr.CustomWorkingDescription !== prev.CustomWorkingDescription;
      });
      
      onlyTypedFieldsChanged = hasTypedFieldChanges && !hasStructuralChanges;
    }

    // Function to send data to parent
    const sendDataToParent = () => {
      
      prevDataRef.current = currentDataStr;
      
      // Flag rows that are still missing a required field (service / date /
      // time-units / amount) so the workflow sidebar can flag the Items step.
      const hasIncompleteRows = tableRows.some((row) => {
        const needsServiceDropdown = isRutOrRotDeduction && (row.pricingType === 'Tim' || !row.pricingType);
        const serviceEmpty = needsServiceDropdown
          ? (!row.service || row.service.trim() === '')
          : (!row.customWorkingDescription || row.customWorkingDescription.trim() === '');
        const dateEmpty = !row.date || row.date.toString().trim() === '';
        const timeEmpty = !row.time || row.time.toString().trim() === '' || parseFloat(row.time) === 0;
        const priceEmpty = !row.price || row.price.toString().trim() === '' || parseFloat(row.price) === 0;
        return serviceEmpty || dateEmpty || timeEmpty || priceEmpty;
      });

      // Send data to parent component
      onServiceTableDataChange({
        totalAmount,
        hourlyWagesAmount,
        workingHours,
        hasIncompleteRows,
      });
    };

    // Only send data if it actually changed
    if (prevDataRef.current !== currentDataStr) {
      // Clear any existing debounce timer
      if (customServiceDebounceTimerRef.current) {
        clearTimeout(customServiceDebounceTimerRef.current);
        customServiceDebounceTimerRef.current = null;
      }
      
      if (onlyTypedFieldsChanged) {
        // Debounce for typed field changes: price, time, custom description (4 seconds)
        customServiceDebounceTimerRef.current = setTimeout(() => {
          sendDataToParent();
          customServiceDebounceTimerRef.current = null;
        }, 2000);
      } else {
        // Immediate update for structural changes (row add/remove, date, dropdown selection)
        sendDataToParent();
      }
    }
    
  }, [tableRows, globalData, onServiceTableDataChange, isHourlyPrice, isRutOrRotDeduction, t]);

  // Cleanup debounce timer only on unmount (not on every re-run)
  useEffect(() => {
    return () => {
      if (customServiceDebounceTimerRef.current) {
        clearTimeout(customServiceDebounceTimerRef.current);
      }
    };
  }, []);


  // Define service options and housework type mappings based on deduction type
  const getRutRotServiceOptionsAndMapping = () => {
    if (deductionType === 'rot') {
      // ROT Service Options
      const options = [
        { value: 'construction', label: t('BUILD') },
        { value: 'electricity', label: t('ELECTRICITY') },
        { value: 'glassmetalwork', label: t('GLASS_SHEET_METAL') },
        { value: 'grounddrainagework', label: t('SOIL_DRAINAGE') },
        { value: 'masonry', label: t('MASONRY') },
        { value: 'paintingwallpapering', label: t('PAINTING_WALLPAPERING') },
        { value: 'hvac', label: t('PLUMBING') }
      ];
      
      const mapping: { [key: string]: string } = {
        'construction': 'CONSTRUCTION',
        'electricity': 'ELECTRICITY',
        'glassmetalwork': 'GLASSMETALWORK',
        'grounddrainagework': 'GROUNDDRAINAGEWORK',
        'masonry': 'MASONRY',
        'paintingwallpapering': 'PAINTINGWALLPAPERING',
        'hvac': 'HVAC'
      };
      
      return { options, mapping };
    } else if (deductionType === 'rut') {
      // RUT Service Options
      const options = [
        { value: 'cleaning', label: t('CLEANING') },
        { value: 'textileclothing', label: t('TEXTILE_CLOTHING') },
        { value: 'snowplowing', label: t('SNOW_PLOWING') },
        { value: 'gardening', label: t('GARDENING') },
        { value: 'babysitting', label: t('BABYSITTING') },
        { value: 'othercare', label: t('OTHER_CARE') },
        { value: 'movingservices', label: t('MOVING_SERVICES') },
        { value: 'itservices', label: t('IT_SERVICES') },
        { value: 'majorappliancerepair', label: t('MAJOR_APPLIANCE_REPAIR') },
        { value: 'furnishing', label: t('FURNISHING') },
        { value: 'homemaintenance', label: t('HOME_MAINTENANCE') },
        { value: 'transportationservices', label: t('TRANSPORTATION_SERVICES') },
        { value: 'washingandcareofclothing', label: t('WASHING_AND_CARE_OF_CLOTHING') }
      ];
      
      const mapping: { [key: string]: string } = {
        'cleaning': 'CLEANING',
        'textileclothing': 'TEXTILECLOTHING',
        'snowplowing': 'SNOWPLOWING',
        'gardening': 'GARDENING',
        'babysitting': 'BABYSITTING',
        'othercare': 'OTHERCARE',
        'movingservices': 'MOVINGSERVICES',
        'itservices': 'ITSERVICES',
        'majorappliancerepair': 'MAJORAPPLIANCEREPAIR',
        'furnishing': 'FURNISHING',
        'homemaintenance': 'HOMEMAINTENANCE',
        'transportationservices': 'TRANSPORTATIONSERVICES',
        'washingandcareofclothing': 'WASHINGANDCAREOFCLOTHING'
      };
      
      return { options, mapping };
    }
    
    // Default empty if no deduction type
    return { options: [], mapping: {} };
  };

  const { options: rutServiceOptions, mapping: houseworkTypeMapping } = getRutRotServiceOptionsAndMapping();

  // Validation function to check if all service descriptions are filled
  const validateServiceDescriptionsFunction = (): boolean => {
    if (tableRows.length === 0) {
      return true; // No rows to validate
    }

    const invalidRowIds: string[] = [];

    for (const row of tableRows) {
      // Check if ROT/RUT dropdown should be shown (same logic as UI rendering)
      const shouldShowRutRotDropdown = isRutOrRotDeduction && (row.pricingType === 'Tim' || !row.pricingType);
      
      if (shouldShowRutRotDropdown) {
        // For ROT/RUT with Hourly (Tim) pricing: service dropdown must be selected
        if (!row.service || row.service.trim() === '') {
          invalidRowIds.push(row.id);
        }
      } else {
        // For all other cases (non-ROT/RUT OR non-Hourly pricing): customWorkingDescription must be filled
        if (!row.customWorkingDescription || row.customWorkingDescription.trim() === '') {
          invalidRowIds.push(row.id);
        }
      }
    }

    if (invalidRowIds.length > 0) {
      // Trigger blinking for invalid rows
      setBlinkingRows(new Set(invalidRowIds));
      
      // Scroll to first invalid field
      setTimeout(() => {
        const firstInvalidRowId = invalidRowIds[0];
        const firstInvalidInput = inputRefs.current[firstInvalidRowId];
        if (firstInvalidInput) {
          firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalidInput.focus();
        }
      }, 100);
      
      return false;
    }

    return true;
  };

  // Expose validation function to parent via ref (keyed by freelancerId)
  useEffect(() => {
    if (validateServiceDescriptions) {
      if (!validateServiceDescriptions.current) {
        validateServiceDescriptions.current = {};
      }
      validateServiceDescriptions.current[freelancerId] = validateServiceDescriptionsFunction;
    }
    
    // Cleanup: remove validation function when component unmounts
    return () => {
      if (validateServiceDescriptions?.current && freelancerId) {
        delete validateServiceDescriptions.current[freelancerId];
      }
    };
  }, [tableRows, isRutOrRotDeduction, validateServiceDescriptions, freelancerId]);

  // Validate that every row has a date filled
  const validateDatesFunction = (): boolean => {
    if (tableRows.length === 0) return true;

    const invalidRowIds: string[] = [];
    for (const row of tableRows) {
      if (!row.date || row.date.trim() === '') {
        invalidRowIds.push(row.id);
      }
    }

    if (invalidRowIds.length > 0) {
      setDateErrorRows(new Set(invalidRowIds));
      setTimeout(() => {
        const firstInvalidEl = dateInputRefs.current[invalidRowIds[0]];
        if (firstInvalidEl) {
          firstInvalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalidEl.focus();
        }
      }, 100);
      return false;
    }

    return true;
  };

  // Expose date validation function to parent via ref
  useEffect(() => {
    if (validateDates) {
      if (!validateDates.current) {
        validateDates.current = {};
      }
      validateDates.current[freelancerId] = validateDatesFunction;
    }

    return () => {
      if (validateDates?.current && freelancerId) {
        delete validateDates.current[freelancerId];
      }
    };
  }, [tableRows, validateDates, freelancerId]);

  // Cleanup validation timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all validation timeouts when component unmounts
      Object.values(validationTimeoutRefs.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle service dropdown
      if (openDropdownRowId) {
        const dropdownRef = dropdownRefs.current[openDropdownRowId];
        const portalList = servicePortalListRef.current[openDropdownRowId];
        const clickedInsideTrigger = dropdownRef && dropdownRef.contains(event.target as Node);
        const clickedInsidePortal = portalList && portalList.contains(event.target as Node);
        if (!clickedInsideTrigger && !clickedInsidePortal) {
          setOpenDropdownRowId(null);
        }
      }
      
      // Handle pricing type dropdown
      if (openPricingTypeRowId) {
        const pricingTypeRef = pricingTypeRefs.current[openPricingTypeRowId];
        const portalList = pricingPortalListRef.current[openPricingTypeRowId];
        const clickedInsideTrigger = pricingTypeRef && pricingTypeRef.contains(event.target as Node);
        const clickedInsidePortal = portalList && portalList.contains(event.target as Node);
        if (!clickedInsideTrigger && !clickedInsidePortal) {
          setOpenPricingTypeRowId(null);
        }
      }
      
      // Handle regular service dropdown
      if (openServiceDropdownRowId) {
        const serviceDropdownRef = serviceDropdownRefs.current[openServiceDropdownRowId];
        if (serviceDropdownRef && !serviceDropdownRef.contains(event.target as Node)) {
          setOpenServiceDropdownRowId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownRowId, openPricingTypeRowId, openServiceDropdownRowId]);

  // Clear service field when switching between RUT/ROT and non-RUT/ROT
  useEffect(() => {
    tableRows.forEach(row => {
       // If RUT/ROT deduction state has changed, clear the service field
      if (row.service) {
        // Check if the current service value is from RUT/ROT options
        const isRutRotService = rutServiceOptions.some(opt => opt.value === row.service);
        
        // If we switched to RUT/ROT mode but service is not a RUT/ROT option, clear it
        if (isRutOrRotDeduction && !isRutRotService) {
          onRowChange(row.id, 'service', '');
        }
        // If we switched out of RUT/ROT mode but service is a RUT/ROT option, clear it
        else if (!isRutOrRotDeduction && isRutRotService) {
          onRowChange(row.id, 'service', '');
        }
      }
    });
  }, [isRutOrRotDeduction]);

  // Helper function to format time input to hh:mm
  const formatTimeInput = (value: string): string => {
    if (!value) return '';

    // Accept comma (,) and dot (.) as the hours:minutes separator, same as colon.
    // e.g. typing "10.45" or "10,45" both become "10:45" (10 hours 45 minutes).
    // Only digits and the separator are kept.
    let cleaned = value.replace(/[.,]/g, ':').replace(/[^\d:]/g, '');

    // Keep only the first separator if the user typed more than one
    const firstColon = cleaned.indexOf(':');
    if (firstColon !== -1) {
      cleaned = cleaned.slice(0, firstColon + 1) + cleaned.slice(firstColon + 1).replace(/:/g, '');
    }

    // If it contains a separator, limit hours to 3 digits (max 999) and minutes to 2 digits
    if (cleaned.includes(':')) {
      const parts = cleaned.split(':');
      const hours = parts[0].slice(0, 3);
      const minutes = (parts[1] || '').slice(0, 2);
      return hours + ':' + minutes;
    }

    // If no separator and length > 2, auto-add colon (HHMM -> HH:MM)
    if (cleaned.length > 2) {
      const hours = cleaned.slice(0, 2);
      const minutes = cleaned.slice(2, 4);
      return `${hours}:${minutes}`;
    }

    return cleaned;
  };

  // Format the on-screen value while PRESERVING whichever separator the user typed (':' ',' or '.').
  // Used only for display; the underlying row.time is still normalized to colon via formatTimeInput.
  const formatTimeDisplay = (value: string): string => {
    if (!value) return '';
    // Keep only digits and separators
    const cleaned = value.replace(/[^\d:.,]/g, '');
    const sepIndex = cleaned.search(/[:.,]/);
    if (sepIndex !== -1) {
      const sep = cleaned[sepIndex];
      const hoursPart = cleaned.slice(0, sepIndex).replace(/[:.,]/g, '').slice(0, 3);
      const minsPart = cleaned.slice(sepIndex + 1).replace(/[:.,]/g, '').slice(0, 2);
      return hoursPart + sep + minsPart;
    }
    // No separator typed yet: auto-insert a colon once there are more than 2 digits (HHMM -> HH:MM)
    if (cleaned.length > 2) {
      return cleaned.slice(0, 2) + ':' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  // Resolve what the time field should show for a row.
  // Prefer the user's original separator, but only while it still represents the SAME time as row.time
  // (guards against showing a stale value if row.time was changed elsewhere, e.g. pricing-type reset).
  const getTimeDisplayValue = (row: TableRow): string => {
    const canonical = normalizeTimeString(row.time || '');
    const rawOverride = rawTimeInputs[row.id];
    if (
      isHourlyPrice &&
      rawOverride !== undefined &&
      rawOverride.replace(/[.,]/g, ':') === canonical
    ) {
      return rawOverride;
    }
    return canonical;
  };

  // Helper function to validate and format time on blur
  const validateAndFormatTime = (value: string): string => {
    if (!value) return '';

    // Treat comma (,) as the hours:minutes separator, same as colon (e.g. "10,45" -> "10:45").
    value = value.replace(/,/g, ':');

    let hours = 0;
    let minutes = 0;

    // Check if value is a decimal number (e.g., 8.5, 10.75, 08.5, 5.30)
    // This handles both "8.5" and "08.5" formats
    if (value.includes('.') && !value.includes(':')) {
      const parts = value.split('.');
      const wholeHours = parseInt(parts[0] || '0');
      const decimal = parseInt(parts[1] || '0');

      if (decimal <= 59) {
        // User means HH.MM format (5.30 = 5 hours 30 minutes)
        hours = wholeHours;
        minutes = decimal;
      } else {
        // Treat as decimal hours (5.75 = 5.75 hours = 5:45)
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue > 0) {
          [hours, minutes] = hoursToTime(numericValue).split(':').map(Number);
        } else {
          hours = wholeHours;
        }
      }
    } else if (value.includes(':')) {
      const [hoursStr, minutesStr] = value.split(':');
      hours = parseInt(hoursStr) || 0;
      minutes = parseInt(minutesStr || '0') || 0;
    } else {
      // If no colon, treat as HHMM format
      const digits = value.replace(/\D/g, '');
      if (digits.length === 0) return '';

      hours = parseInt(digits.slice(0, 2)) || 0;
      minutes = digits.length > 2 ? parseInt(digits.slice(2, 4)) || 0 : 0;
    }

    // Validate ranges (hours allowed up to 999, minutes unchanged)
    hours = Math.min(Math.max(hours, 0), 999);
    minutes = Math.min(Math.max(minutes, 0), 59);
    const rounded = roundToQuarterHour(hours, minutes, 999);
    // Format with leading zeros
    return `${rounded.hours.toString().padStart(2, '0')}:${rounded.minutes.toString().padStart(2, '0')}`;
  };

  // Handle time input change
  const handleTimeChange = (rowId: string, value: string) => {
   
    // Allow typing freely but format as user types
    const formatted = formatTimeInput(value);
  
    onRowChange(rowId, 'time', formatted);
    };

  // Handle time input blur to validate
  const handleTimeBlur = (rowId: string, value: string) => {

    const validated = validateAndFormatTime(value);
    if (validated) {
      onRowChange(rowId, 'time', validated);
      }
  };

  // ── Split time entry: an hours field + a quarter-hour minutes dropdown ──────
  // row.time is STILL stored canonically as "HH:MM", so every calculation and the
  // payload are unaffected — only how the user enters minutes changed.
  const QUARTER_MINUTES = ['00', '15', '30', '45'];
  const getRowHours = (row: TableRow): string => {
    const canonical = normalizeTimeString(row.time || '');
    if (!canonical || canonical.trim() === '') return '';
    const h = (canonical.split(':')[0] ?? '').trim();
    if (h === '') return '';
    const n = parseInt(h, 10);
    return isNaN(n) ? '' : String(n);
  };
  const getRowMinutes = (row: TableRow): string => {
    const canonical = normalizeTimeString(row.time || '');
    const raw = parseInt((canonical.split(':')[1] ?? '0'), 10) || 0;
    // snap to the nearest available quarter for the dropdown
    return QUARTER_MINUTES.reduce(
      (best, opt) => (Math.abs(Number(opt) - raw) < Math.abs(Number(best) - raw) ? opt : best),
      '00'
    );
  };
  const combineHoursMinutes = (hours: string, minutes: string): string => {
    const h = String(Math.min(Math.max(parseInt(hours || '0', 10) || 0, 0), 999)).padStart(2, '0');
    const m = QUARTER_MINUTES.includes(minutes) ? minutes : '00';
    return `${h}:${m}`;
  };

  // Helper function to format date for date input (YYYY-MM-DD).
  // Parses the known formats EXPLICITLY (no `new Date(string)`, which shifts the
  // day by timezone/locale) so the DatePicker shows the exact stored day.
  const formatDateForInput = (dateStr: string): string => {
    if (!dateStr) return '';

    let m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);            // already YYYY-MM-DD
    if (m) return dateStr;
    m = dateStr.match(/^(\d{4})[/](\d{1,2})[/](\d{1,2})$/);        // YYYY/MM/DD
    if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    m = dateStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);      // MM/DD/YYYY or MM-DD-YYYY
    if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;

    // Fallback (last resort) — derive from a Date using LOCAL parts.
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  return (
    <div className="services-table-container">
      <div className="table-responsive-wrapper" ref={tableScrollRef}>
        {/* Desktop Table Header - Hidden on mobile */}
        <div className="services-table-header  d-lg-flex d-none">
          {/* <div className="col-drag-header"></div> */}
          <div className="col-dates-header">{t('DATE_S_HEADER')}</div>
          <div className="col-pricing-type-header">{t('PRICING_TYPE')}</div>
          <div className="col-service-header">{t('SERVICE')}</div>
          <div className="col-time-header">{isHourlyPrice ? t('TIMES') : t('UNITS')}</div>
          <div className="col-price-header">{t('PRICE')}</div>
          {/* <div className="col-discount-header">{t('DISCOUNT')}</div> */}
          <div className="col-subtotal-header">{t('SUBTOTAL')}</div>
          <div className="col-actions-header"></div>
        </div>
      
      <div className="services-table-body">
        {tableRows.length === 0 ? (
          // Empty state row
          <div className="services-table-row d-flex">
            {/* <div className="col-drag-cell d-none d-lg-flex">
             <svg xmlns="http://www.w3.org/2000/svg" width="15" height="24" viewBox="0 0 15 24" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0 2.25C-8.80582e-09 1.95453 0.0581978 1.66194 0.171271 1.38896C0.284344 1.11598 0.450078 0.867941 0.659009 0.659009C0.867941 0.450078 1.11598 0.284344 1.38896 0.171271C1.66194 0.0581978 1.95453 0 2.25 0C2.54547 0 2.83806 0.0581978 3.11104 0.171271C3.38402 0.284344 3.63206 0.450078 3.84099 0.659009C4.04992 0.867941 4.21566 1.11598 4.32873 1.38896C4.4418 1.66194 4.5 1.95453 4.5 2.25C4.5 2.84674 4.26295 3.41903 3.84099 3.84099C3.41903 4.26295 2.84674 4.5 2.25 4.5C1.65326 4.5 1.08097 4.26295 0.659009 3.84099C0.237053 3.41903 1.77841e-08 2.84674 0 2.25ZM10.5 2.25C10.5 1.65326 10.7371 1.08097 11.159 0.659009C11.581 0.237053 12.1533 0 12.75 0C13.3467 0 13.919 0.237053 14.341 0.659009C14.7629 1.08097 15 1.65326 15 2.25C15 2.84674 14.7629 3.41903 14.341 3.84099C13.919 4.26295 13.3467 4.5 12.75 4.5C12.1533 4.5 11.581 4.26295 11.159 3.84099C10.7371 3.41903 10.5 2.84674 10.5 2.25ZM0 11.985C-8.80582e-09 11.6895 0.0581978 11.3969 0.171271 11.124C0.284344 10.851 0.450078 10.6029 0.659009 10.394C0.867941 10.1851 1.11598 10.0193 1.38896 9.90627C1.66194 9.7932 1.95453 9.735 2.25 9.735C2.54547 9.735 2.83806 9.7932 3.11104 9.90627C3.38402 10.0193 3.63206 10.1851 3.84099 10.394C4.04992 10.6029 4.21566 10.851 4.32873 11.124C4.4418 11.3969 4.5 11.6895 4.5 11.985C4.5 12.5817 4.26295 13.154 3.84099 13.576C3.41903 13.9979 2.84674 14.235 2.25 14.235C1.65326 14.235 1.08097 13.9979 0.659009 13.576C0.237053 13.154 1.77841e-08 12.5817 0 11.985ZM10.5 11.985C10.5 11.3883 10.7371 10.816 11.159 10.394C11.581 9.97205 12.1533 9.735 12.75 9.735C13.3467 9.735 13.919 9.97205 14.341 10.394C14.7629 10.816 15 11.3883 15 11.985C15 12.5817 14.7629 13.154 14.341 13.576C13.919 13.9979 13.3467 14.235 12.75 14.235C12.1533 14.235 11.581 13.9979 11.159 13.576C10.7371 13.154 10.5 12.5817 10.5 11.985ZM0 21.75C-8.80582e-09 21.4545 0.0581978 21.1619 0.171271 20.889C0.284344 20.616 0.450078 20.3679 0.659009 20.159C0.867941 19.9501 1.11598 19.7843 1.38896 19.6713C1.66194 19.5582 1.95453 19.5 2.25 19.5C2.54547 19.5 2.83806 19.5582 3.11104 19.6713C3.38402 19.7843 3.63206 19.9501 3.84099 20.159C4.04992 20.3679 4.21566 20.616 4.32873 20.889C4.4418 21.1619 4.5 21.4545 4.5 21.75C4.5 22.3467 4.26295 22.919 3.84099 23.341C3.41903 23.7629 2.84674 24 2.25 24C1.65326 24 1.08097 23.7629 0.659009 23.341C0.237053 22.919 1.77841e-08 22.3467 0 21.75ZM10.5 21.75C10.5 21.1533 10.7371 20.581 11.159 20.159C11.581 19.7371 12.1533 19.5 12.75 19.5C13.3467 19.5 13.919 19.7371 14.341 20.159C14.7629 20.581 15 21.1533 15 21.75C15 22.3467 14.7629 22.919 14.341 23.341C13.919 23.7629 13.3467 24 12.75 24C12.1533 24 11.581 23.7629 11.159 23.341C10.7371 22.919 10.5 22.3467 10.5 21.75Z" fill="#717680"/>
</svg>
            </div> */}
            
            <div 
              className="col-dates-cell col-12 col-lg-auto"
              onClick={() => {
                if (!onAddRow) return;
                const hasCustomer = customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id;
                if (!hasCustomer) return; // Prevent action if no customer
                
                // Generate row with current date
                const today = new Date();
                const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
                const currentDay = String(today.getDate()).padStart(2, '0');
                const currentYear = today.getFullYear();
                const currentDate = `${currentMonth}/${currentDay}/${currentYear}`;
                
                onAddRow({
                  id: uuidv4(),
                  workingHourId: 0,
                  timeSheetWorkingHourId: 0,
                  date: currentDate,
                  pricingType: 'Tim',
                  pricingTypeArticleNumber: '92',
                  service: '',
                  customWorkingDescription: '',
                  time: '08:00',
                  price: '500',
                  discount: { value: 0, type: 'percentage' as const },
                  subtotal: 0
                });
              }}
              style={{ 
                cursor: customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? 'pointer' : 'not-allowed',
                opacity: customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? 1 : 0.5,
                borderRadius: '8px',
                padding: '8px 12px',
                transition: 'all 0.3s ease',
                // background: shouldButtonBlink ? 'linear-gradient(90deg, rgba(103, 80, 184, 0.05) 0%, rgba(103, 80, 184, 0.15) 100%)' : 'transparent'
              }}
            >
              <style>{`
                @keyframes professionalRowBlink {
                  0%, 100% {
                    color: #6750B8;
                    transform: scale(1);
                    text-shadow: 0 0 0 rgba(103, 80, 184, 0);
                  }
                  50% {
                    color: #8B7DD8;
                    transform: scale(1.05);
                    text-shadow: 0 0 12px rgba(127, 126, 129, 0.6), 0 0 24px rgba(103, 80, 184, 0.3);
                  }
                }
              `}</style>
              <span className="mobile-label d-lg-none fw-bold">{t('DATE_S_HEADER')}: </span>
              <span className="date-placeholder" style={{ 
                color: '#6750b8', 
                fontWeight: '600', 
                position: 'relative', 
                left: '10px',
                animation: shouldButtonBlink ? 'professionalRowBlink 1.5s ease-in-out infinite' : 'none',
                transition: 'all 0.3s ease',
                display: 'inline-block'
              }}>
                {t('GENERATE_ROW') || 'Generate Row'}
              </span>
            </div>
            
            <div className="col-pricing-type-cell col-12 col-lg-auto">
              <div className="pricing-type-input-wrapper">
                <span className="pricing-type-placeholder">{t('HOURLY_WAGES')}</span>
                <svg className="dropdown-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            <div className="col-service-cell">
              {isRutOrRotDeduction ? (
                <div className="service-input-wrapper">
                  <span className="service-placeholder">{t('SEARCH_SELECT_SERVICE')}</span>
                  <svg className="dropdown-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              ) : (
                <div className="service-input-wrapper">
                  <span className="service-placeholder">{t('ENTER_SERVICE_NAME')}</span>
                </div>
              )}
            </div>
            
            <div className="col-time-cell">
              <div className="time-input-wrapper">
                <span className="time-placeholder">{isHourlyPrice ? '08:00' : t('UNITS')}</span>
              </div>
            </div>
            
            <div className="col-price-cell">
              <div className="price-input-wrapper">
                <span className="price-placeholder">500</span>
                <span className="currency-symbol">{currency || 'SEK'}</span>
              </div>
            </div>
            
            {/* <div className="col-discount-cell">
              <div className="discount-wrapper">
                <div className="discount-input-part">
                  <span className="discount-placeholder"></span>
                </div>
                <div className="discount-dropdown-part">
                  <span className="discount-type">%</span>
                  <svg className="dropdown-arrow-small" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div> */}
            
            <div className="col-subtotal-cell">
              <span className="subtotal-value">{currency || 'SEK'}</span>
            </div>
            
            <div className="col-actions-cell">
              <Button variant="link" className="remove-btn" disabled>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
            </div>
          </div>
        ) : (
          // Dynamic rows
          tableRows.map((row, index) => {
            // Missing required fields for this row → show a red inline error
            // telling the user exactly what to fill (date / service / time / amount).
            const needsServiceDropdown = isRutOrRotDeduction && (row.pricingType === 'Tim' || !row.pricingType);
            const serviceEmpty = needsServiceDropdown
              ? (!row.service || row.service.trim() === '')
              : (!row.customWorkingDescription || row.customWorkingDescription.trim() === '');
            const dateEmpty = !row.date || row.date.toString().trim() === '';
            const timeEmpty = !row.time || row.time.toString().trim() === '' || parseFloat(row.time) === 0;
            const priceEmpty = !row.price || row.price.toString().trim() === '' || parseFloat(row.price) === 0;
            const rowErrs: string[] = [];
            if (dateEmpty) rowErrs.push(t('ST_ROW_DATE') || 'date');
            if (serviceEmpty) rowErrs.push(t('ST_ROW_SERVICE') || 'service description');
            if (timeEmpty) rowErrs.push(isHourlyPrice ? (t('ST_ROW_TIME') || 'time') : (t('ST_ROW_UNIT') || 'units'));
            if (priceEmpty) rowErrs.push(t('ST_ROW_AMOUNT') || 'amount');
            return (
            <React.Fragment key={row.id}>
            <div className="services-table-row d-flex flex-wrap flex-lg-nowrap">
              {/* <div className="col-drag-cell d-none d-lg-flex">
               <svg xmlns="http://www.w3.org/2000/svg" width="15" height="20" viewBox="0 0 15 24" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0 2.25C-8.80582e-09 1.95453 0.0581978 1.66194 0.171271 1.38896C0.284344 1.11598 0.450078 0.867941 0.659009 0.659009C0.867941 0.450078 1.11598 0.284344 1.38896 0.171271C1.66194 0.0581978 1.95453 0 2.25 0C2.54547 0 2.83806 0.0581978 3.11104 0.171271C3.38402 0.284344 3.63206 0.450078 3.84099 0.659009C4.04992 0.867941 4.21566 1.11598 4.32873 1.38896C4.4418 1.66194 4.5 1.95453 4.5 2.25C4.5 2.84674 4.26295 3.41903 3.84099 3.84099C3.41903 4.26295 2.84674 4.5 2.25 4.5C1.65326 4.5 1.08097 4.26295 0.659009 3.84099C0.237053 3.41903 1.77841e-08 2.84674 0 2.25ZM10.5 2.25C10.5 1.65326 10.7371 1.08097 11.159 0.659009C11.581 0.237053 12.1533 0 12.75 0C13.3467 0 13.919 0.237053 14.341 0.659009C14.7629 1.08097 15 1.65326 15 2.25C15 2.84674 14.7629 3.41903 14.341 3.84099C13.919 4.26295 13.3467 4.5 12.75 4.5C12.1533 4.5 11.581 4.26295 11.159 3.84099C10.7371 3.41903 10.5 2.84674 10.5 2.25ZM0 11.985C-8.80582e-09 11.6895 0.0581978 11.3969 0.171271 11.124C0.284344 10.851 0.450078 10.6029 0.659009 10.394C0.867941 10.1851 1.11598 10.0193 1.38896 9.90627C1.66194 9.7932 1.95453 9.735 2.25 9.735C2.54547 9.735 2.83806 9.7932 3.11104 9.90627C3.38402 10.0193 3.63206 10.1851 3.84099 10.394C4.04992 10.6029 4.21566 10.851 4.32873 11.124C4.4418 11.3969 4.5 11.6895 4.5 11.985C4.5 12.5817 4.26295 13.154 3.84099 13.576C3.41903 13.9979 2.84674 14.235 2.25 14.235C1.65326 14.235 1.08097 13.9979 0.659009 13.576C0.237053 13.154 1.77841e-08 12.5817 0 11.985ZM10.5 11.985C10.5 11.3883 10.7371 10.816 11.159 10.394C11.581 9.97205 12.1533 9.735 12.75 9.735C13.3467 9.735 13.919 9.97205 14.341 10.394C14.7629 10.816 15 11.3883 15 11.985C15 12.5817 14.7629 13.154 14.341 13.576C13.919 13.9979 13.3467 14.235 12.75 14.235C12.1533 14.235 11.581 13.9979 11.159 13.576C10.7371 13.154 10.5 12.5817 10.5 11.985ZM0 21.75C-8.80582e-09 21.4545 0.0581978 21.1619 0.171271 20.889C0.284344 20.616 0.450078 20.3679 0.659009 20.159C0.867941 19.9501 1.11598 19.7843 1.38896 19.6713C1.66194 19.5582 1.95453 19.5 2.25 19.5C2.54547 19.5 2.83806 19.5582 3.11104 19.6713C3.38402 19.7843 3.63206 19.9501 3.84099 20.159C4.04992 20.3679 4.21566 20.616 4.32873 20.889C4.4418 21.1619 4.5 21.4545 4.5 21.75C4.5 22.3467 4.26295 22.919 3.84099 23.341C3.41903 23.7629 2.84674 24 2.25 24C1.65326 24 1.08097 23.7629 0.659009 23.341C0.237053 22.919 1.77841e-08 22.3467 0 21.75ZM10.5 21.75C10.5 21.1533 10.7371 20.581 11.159 20.159C11.581 19.7371 12.1533 19.5 12.75 19.5C13.3467 19.5 13.919 19.7371 14.341 20.159C14.7629 20.581 15 21.1533 15 21.75C15 22.3467 14.7629 22.919 14.341 23.341C13.919 23.7629 13.3467 24 12.75 24C12.1533 24 11.581 23.7629 11.159 23.341C10.7371 22.919 10.5 22.3467 10.5 21.75Z" fill="#717680"/>
</svg>
              </div> */}
              
              <div className="col-dates-cell col-12 col-lg-auto">
                <span className="mobile-label d-lg-none fw-bold">{t('DATE_S_HEADER')}: </span>
                <div
                  className="date-input-wrapper st-date"
                  ref={(el: HTMLDivElement | null) => { dateInputRefs.current[row.id] = el; }}
                >
                  <DatePicker
                    selected={parseIso(formatDateForInput(row.date))}
                    onChange={(d: Date | null) => {
                      const iso = toIso(d);
                      onRowChange(row.id, 'date', iso);
                      if (iso && dateErrorRows.has(row.id)) {
                        setDateErrorRows(prev => {
                          const next = new Set(prev);
                          next.delete(row.id);
                          return next;
                        });
                      }
                    }}
                    dateFormat="yyyy/MM/dd"
                    placeholderText="yyyy/mm/dd"
                    className={`date-input st-date__input ${dateErrorRows.has(row.id) ? 'error-border field-blink' : ''}`}
                    wrapperClassName="st-date__input-dp"
                    popperContainer={PopperPortal}
                    popperProps={{ strategy: 'fixed' }}
                    popperClassName="st-date__dp-popper"
                    calendarClassName="st-date__dp-calendar"
                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="scroll"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              <div className="col-pricing-type-cell col-12 col-lg-auto">
                <span className="mobile-label d-lg-none fw-bold">{t('PRICING_TYPE')}: </span>
                <div
                  className="pricing-type-custom-dropdown"
                  ref={(el) => pricingTypeRefs.current[row.id] = el}
                >
                  <div 
                    className={`dropdown-trigger ${openPricingTypeRowId === row.id ? 'open' : ''}`}
                    onClick={() => setOpenPricingTypeRowId(openPricingTypeRowId === row.id ? null : row.id)}
                  >
                    <span className="dropdown-value">
                      {row.pricingType 
                        ? t(PRICING_TYPE_OPTIONS.find(opt => opt.value === row.pricingType)?.articleName || 'HOURLY_WAGES') 
                        : t('HOURLY_WAGES')}
                    </span>
                    <svg 
                      className={`dropdown-arrow ${openPricingTypeRowId === row.id ? 'rotated' : ''}`}
                      width="20" 
                      height="20" 
                      viewBox="0 0 20 20" 
                      fill="none"
                    >
                      <path 
                        d="M5 7.5L10 12.5L15 7.5" 
                        stroke="currentColor" 
                        strokeWidth="1.67" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  
                  {openPricingTypeRowId === row.id && ReactDOM.createPortal(
                    <div className="pricing-type-custom-dropdown" style={{ position: 'static' }}>
                    <div 
                      ref={(el) => { pricingPortalListRef.current[row.id] = el; }}
                      className="dropdown-list"
                      style={{
                        position: 'fixed',
                        top: dropdownPositions[row.id]?.top || 0,
                        left: dropdownPositions[row.id]?.left || 0,
                        width: dropdownPositions[row.id]?.width || 250,
                        zIndex: 9999,
                        maxWidth: '350px'
                      }}
                    >
                      <div className="dropdown-content">
                        {PRICING_TYPE_OPTIONS.map((option) => (
                          <div
                            key={option.id}
                            className={`list-item ${(row.pricingType || 'Tim') === option.value ? 'selected' : ''}`}
                            onClick={() => {
                              // Set both pricingType value and articleNumber
                              onRowChange(row.id, 'pricingType', option.value);
                              onRowChange(row.id, 'pricingTypeArticleNumber', option.articleNumber);
                              
                              // If pricing type is not "Tim" (hour), set time to "1" (default 1 unit)
                              if (option.value !== 'Tim') {
                                onRowChange(row.id, 'time', '1');
                              }
                              // Payment Per Unit: also clear any decimal value to ensure integer only
                              if (option.value === 'st') {
                                onRowChange(row.id, 'time', '1');
                              }
                              
                              setOpenPricingTypeRowId(null);
                            }}
                          >
                            <div className="text-input">
                              <div className="paragraph-container">
                                <p className="option-text">{t(option.articleName)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    </div>
                  , document.body)}
                </div>
              </div>
              
              <div className="col-service-cell col-12 col-lg-auto">
                <span className="mobile-label d-lg-none fw-bold">{t('SERVICE')}: </span>
                {isRutOrRotDeduction && (row.pricingType === 'Tim' || !row.pricingType) ? (
                  // Show dropdown for ROT/RUT only when pricing type is "Tim" (Hourly) or not set
                  <div 
                    className="service-custom-dropdown" 
                    ref={(el) => {
                      dropdownRefs.current[row.id] = el;
                      if (el) inputRefs.current[row.id] = el.querySelector('.dropdown-trigger') as HTMLInputElement;
                    }}
                  >
                    <div 
                      className={`dropdown-trigger ${openDropdownRowId === row.id ? 'open' : ''} ${serviceEmpty ? 'error-border' : ''} ${blinkingRows.has(row.id) ? 'field-blink' : ''}`}
                      onClick={() => {
                        // Clear blinking when user interacts
                        if (blinkingRows.has(row.id)) {
                          setBlinkingRows(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(row.id);
                            return newSet;
                          });
                        }
                        setOpenDropdownRowId(openDropdownRowId === row.id ? null : row.id);
                      }}
                    >
                      <span className={`dropdown-value ${!row.service ? 'placeholder' : ''}`}>
                        {row.service ? rutServiceOptions.find(opt => opt.value === row.service)?.label : t('SEARCH_SELECT_SERVICE')}
                      </span>
                      <svg 
                        className={`dropdown-arrow ${openDropdownRowId === row.id ? 'rotated' : ''}`}
                        width="20" 
                        height="20" 
                        viewBox="0 0 20 20" 
                        fill="none"
                      >
                        <path 
                          d="M5 7.5L10 12.5L15 7.5" 
                          stroke="currentColor" 
                          strokeWidth="1.67" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    {openDropdownRowId === row.id && ReactDOM.createPortal(
                      <div className="service-custom-dropdown" style={{ position: 'static' }}>
                      <div 
                        ref={(el) => { servicePortalListRef.current[row.id] = el; }}
                        className="dropdown-list"
                        style={{
                          position: 'fixed',
                          top: dropdownPositions[row.id]?.top || 0,
                          left: dropdownPositions[row.id]?.left || 0,
                          width: dropdownPositions[row.id]?.width || 250,
                          zIndex: 9999,
                          maxWidth: '300px',
                          minWidth: '250px'
                        }}
                      >
                        <div className="dropdown-content">
                          {rutServiceOptions.map((option) => (
                            <div
                              key={option.value}
                              className={`list-item ${row.service === option.value ? 'selected' : ''}`}
                              onClick={() => {
                                onRowChange(row.id, 'service', option.value);
                                // Clear blinking when user selects an option
                                if (blinkingRows.has(row.id)) {
                                  setBlinkingRows(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(row.id);
                                    return newSet;
                                  });
                                }
                                setOpenDropdownRowId(null);
                              }}
                            >
                              <div className="text-input">
                                <div className="paragraph-container">
                                  <p className="option-text">{option.label}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      </div>
                    , document.body)}
                  </div>
                ) : (
                  // Show input field for non-ROT/RUT OR for any pricing type other than "Tim" (Hourly)
                  <div style={{ position: 'relative', width: '100%' }}>
                    <Form.Control
                      ref={(el) => { inputRefs.current[row.id] = el; }}
                      type="text"
                      placeholder={t('ENTER_SERVICE_NAME')}
                      value={row.customWorkingDescription || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        // Clear blinking if user starts typing
                        if (value.trim() !== '' && blinkingRows.has(row.id)) {
                          setBlinkingRows(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(row.id);
                            return newSet;
                          });
                        }
                        
                        // Check for non-Latin characters (allows Latin letters, Swedish characters ÅÄÖåäö, numbers, spaces, and common punctuation)
                        const latinPattern = /^[A-Za-zÅÄÖåäö0-9\s\.,!?'"\-()&]*$/;
                        
                        // Limit input to 42 characters
                        const limitedValue = value.slice(0, 42);
                        
                        // Validate for non-Latin characters
                        if (limitedValue && !latinPattern.test(limitedValue)) {
                          // Clear any existing timeout for this row
                          if (validationTimeoutRefs.current[row.id]) {
                            clearTimeout(validationTimeoutRefs.current[row.id]);
                          }
                          
                          // Set error message
                          setValidationErrors(prev => ({
                            ...prev,
                            [row.id]: t('ONLY_LATIN_CHARACTERS_ALLOWED') || 'Only Latin characters are allowed'
                          }));
                          
                          // Auto-hide after 5 seconds
                          validationTimeoutRefs.current[row.id] = setTimeout(() => {
                            setValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors[row.id];
                              return newErrors;
                            });
                            delete validationTimeoutRefs.current[row.id];
                          }, 5000);
                          
                          // Don't update the field with invalid characters
                          return;
                        } else {
                          // Clear error if validation passes
                          if (validationTimeoutRefs.current[row.id]) {
                            clearTimeout(validationTimeoutRefs.current[row.id]);
                            delete validationTimeoutRefs.current[row.id];
                          }
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[row.id];
                            return newErrors;
                          });
                        }
                        
                        // Only update if validation passed
                        onRowChange(row.id, 'customWorkingDescription', limitedValue);
                      }}
                      maxLength={42}
                      className={`service-text-input ${serviceEmpty ? 'error-border' : ''} ${blinkingRows.has(row.id) ? 'field-blink' : ''}`}
                    />
                    {validationErrors[row.id] && (
                      <div style={{
                        position: 'absolute',
                        top: '-45px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#000000ff',
                        color: '#fff', 
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        animation: 'fadeIn 0.2s ease-in'
                      }}>
                        {validationErrors[row.id]}
                        <div style={{
                          position: 'absolute',
                          bottom: '-4px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '5px solid transparent',
                          borderRight: '5px solid transparent',
                          borderTop: '5px solid #000000ff'
                        }} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="col-time-cell">
                <span className="mobile-label d-lg-none fw-bold">{isHourlyPrice ? t('TIMES') : t('UNITS')}: </span>
                {isHourlyPrice && (row.pricingType === 'Tim' || !row.pricingType) ? (
                  // Time = hours field + quarter-hour minutes dropdown (row.time stays "HH:MM")
                  <div className={`time-hm-wrapper${(!row.time || row.time.trim() === '' || parseFloat(row.time) === 0) ? ' input-error-border' : ''}`}>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="time-hours-input"
                      placeholder="08"
                      maxLength={3}
                      value={getRowHours(row)}
                      onChange={(e) => {
                        const h = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                        onRowChange(row.id, 'time', h === '' ? '' : combineHoursMinutes(h, getRowMinutes(row)));
                      }}
                    />
                    <span className="time-hm-colon">:</span>
                    <div
                      className={`time-min-dd${openMinutesRowId === row.id ? ' open' : ''}`}
                      ref={openMinutesRowId === row.id ? minutesDropdownRef : undefined}
                    >
                      <button
                        type="button"
                        className="time-min-trigger"
                        aria-haspopup="listbox"
                        aria-expanded={openMinutesRowId === row.id}
                        aria-label="Minutes"
                        onClick={(e) => {
                          if (openMinutesRowId === row.id) setOpenMinutesRowId(null);
                          else openMinutesDropdown(row.id, e.currentTarget);
                        }}
                      >
                        <span className="time-min-value">{getRowMinutes(row)}</span>
                        <svg className="time-min-chevron" width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {openMinutesRowId === row.id && minutesMenuPos && ReactDOM.createPortal(
                        <div
                          className="time-min-portal-menu"
                          role="listbox"
                          ref={minutesMenuRef}
                          style={{ top: minutesMenuPos.top, left: minutesMenuPos.left, width: minutesMenuPos.width }}
                        >
                          {QUARTER_MINUTES.map((m) => {
                            const isSel = getRowMinutes(row) === m;
                            return (
                              <button
                                type="button"
                                role="option"
                                aria-selected={isSel}
                                key={m}
                                className={`time-min-item${isSel ? ' selected' : ''}`}
                                onClick={() => {
                                  onRowChange(row.id, 'time', combineHoursMinutes(getRowHours(row) || '0', m));
                                  setOpenMinutesRowId(null);
                                }}
                              >
                                {m}
                              </button>
                            );
                          })}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>
                ) : (
                <div className={`time-input-wrapper${(!row.time || row.time.trim() === '' || parseFloat(row.time) === 0) ? ' input-error-border' : ''}`}>
                  <Form.Control
                    type="text"
                    placeholder={row.pricingType === 'st' ? 'Unit' : (isHourlyPrice ? '08:00' : t('ENTER_UNITS'))}
                    value={getTimeDisplayValue(row)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (row.pricingType === 'st') {
                        // Payment Per Unit: only allow integers (no decimals), allow empty while typing
                        const intOnly = val.replace(/[^\d]/g, '');
                        onRowChange(row.id, 'time', intOnly);
                      } else if (row.pricingType === 'Tim' || !row.pricingType) {
                        if (isHourlyPrice) {
                          // Preserve the separator the user typed for display only...
                          setRawTimeInputs(prev => ({ ...prev, [row.id]: formatTimeDisplay(val) }));
                          // ...while row.time stays canonical HH:MM (colon) for calculations/payload
                          handleTimeChange(row.id, val);
                        } else {
                          onRowChange(row.id, 'time', val);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (row.pricingType === 'st') {
                        // Default to '1' if empty on blur
                        if (!e.target.value || e.target.value.trim() === '') {
                          onRowChange(row.id, 'time', '1');
                        }
                      } else if (isHourlyPrice && (row.pricingType === 'Tim' || !row.pricingType)) {
                        handleTimeBlur(row.id, e.target.value);
                        // Re-apply the user's separator to the validated HH:MM value so the field keeps showing it
                        const validated = validateAndFormatTime(e.target.value);
                        if (validated) {
                          const sepMatch = (rawTimeInputs[row.id] || '').match(/[.,]/);
                          setRawTimeInputs(prev => ({
                            ...prev,
                            [row.id]: sepMatch ? validated.replace(':', sepMatch[0]) : validated,
                          }));
                        }
                      }
                    }}
                    className="time-input"
                    maxLength={isHourlyPrice ? 6 : undefined}
                    disabled={!!(row.pricingType && row.pricingType !== 'Tim' && row.pricingType !== 'st')}
                    style={{
                      cursor: (row.pricingType && row.pricingType !== 'Tim' && row.pricingType !== 'st') ? 'not-allowed' : 'text',
                      opacity: (row.pricingType && row.pricingType !== 'Tim' && row.pricingType !== 'st') ? 0.6 : 1
                    }}
                  />
                </div>
                )}
              </div>

              <div className="col-price-cell col-6 col-lg-auto">
                <span className="mobile-label d-lg-none fw-bold">{t('PRICE')}: </span>
                <div className={`price-input-wrapper${(!row.price || row.price.toString().trim() === '' || parseFloat(row.price) === 0) ? ' input-error-border' : ''}`}>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="500"
                    value={row.price ?? ''}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const sanitized = sanitizePriceInput(e.target.value);
                      if (sanitized !== null && (sanitized === '' || parseFloat(sanitized) >= 0)) {
                        // Cap the integer part to 9 digits (≈ 999,999,999) so an
                        // absurdly large price can't blow up the subtotal and
                        // overflow the row. Decimals are still allowed.
                        const intDigits = (sanitized.split('.')[0] || '').replace(/\D/g, '');
                        if (intDigits.length > 9) return;
                        onRowChange(row.id, 'price', sanitized);
                      }
                    }}
                    className="price-input"
                  />
                  <span className="currency-symbol">{currency || 'SEK'}</span>
                </div>
              </div>
              
              {/* <div className="col-discount-cell col-6 col-lg-auto">
                <span className="mobile-label d-lg-none fw-bold">{t('DISCOUNT')}: </span>
                <div className="discount-wrapper">
                  <div className="discount-input-part">
                    <Form.Control
                      type="number"
                      value={row.discount.value || ''}
                      onChange={(e) => onRowChange(row.id, 'discount', { ...row.discount, value: parseFloat(e.target.value) || 0 })}
                      className="discount-input"
                      placeholder=""
                    />
                  </div>
                  <div className="discount-dropdown-part">
                    <span className="discount-type">{row.discount.type === 'percentage' ? '%' : 'SEK'}</span>
                    <svg className="dropdown-arrow-small" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div> */}
              
              <div className="col-subtotal-cell col-12 col-lg-auto">
                <span className="mobile-label d-lg-none fw-bold">{t('SUBTOTAL')}: </span>
                {(() => {
                  const subtotalText = serviceData[row.id]?.subtotal > 0
                    ? formatAmountWithLocale(serviceData[row.id].subtotal, currency || 'SEK', i18n.language)
                    : `${currency || 'SEK'}`;
                  // title → full amount on hover, since the value can be truncated
                  return <span className="subtotal-value" title={subtotalText}>{subtotalText}</span>;
                })()}
              </div>
              
              <div className="col-actions-cell col-12 col-lg-auto d-flex justify-content-end justify-content-lg-center">
                <Button variant="link" className="remove-btn iw-icon-btn" onClick={() => onRemoveRow(row.id)} data-tip={String(t('REMOVE') || 'Remove')} aria-label={String(t('REMOVE') || 'Remove')}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Button>
              </div>
            </div>
            {rowErrs.length > 0 && (
              <div className="st-row-error" role="alert">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 8v5M12 16.5v.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                </svg>
                <span>{t('ST_ROW_FILL') || 'Please add'}: {rowErrs.join(', ')}</span>
              </div>
            )}
            </React.Fragment>
            );
          })
        )}
      </div>
      
      {/* Add New Row Button */}
      {onAddRow && (
        <div className="add-new-row-section" style={{ marginTop: '16px'}}>
          <Button
            variant="link"
            data-tour="tour-add-row"
            disabled={!customerState?.selectedCustomer?.customerId && !customerState?.selectedCustomer?.id}
            onClick={() => {
              const hasCustomer = customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id;
              if (!hasCustomer) return; // Prevent action if no customer
              
              // Always pass date:'' — the parent handler computes the date from
              // React's freshest state (next day after the latest row, or today
              // for the first). `tableRows` here can lag a render, so we must NOT
              // branch on its length (that caused a "today" row every time).
              const lastRow = tableRows.length ? tableRows[tableRows.length - 1] : null;
              const source = lastRow
                ? { ...lastRow }
                : {
                    pricingType: 'Tim',
                    pricingTypeArticleNumber: '92',
                    service: '',
                    customWorkingDescription: '',
                    time: '08:00',
                    price: '500',
                    discount: { value: 0, type: 'percentage' as const },
                    subtotal: 0,
                  };
              onAddRow({
                ...source,
                date: '', // parent computes the correct date
                id: uuidv4(),
                workingHourId: 0,
                timeSheetWorkingHourId: 0,
              } as any);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 4px',
              border: 'none',
              background: 'transparent',
              color: customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? '#6750b8' : '#9CA3AF',
              fontSize: '14px',
              fontWeight: 500,
              cursor: customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? 'pointer' : 'not-allowed',
              textDecoration: 'none',
              transition: 'all 0.2s',
              opacity: customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id) {
                e.currentTarget.style.opacity = '0.8';
              }
            }}
            onMouseLeave={(e) => {
              if (customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id) {
                e.currentTarget.style.opacity = '1';
              }
            }}
            title="Add new working hour with next date"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" stroke={customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? '#6750b8' : '#9CA3AF'} strokeWidth="1.5" fill="none"/>
              <path d="M10 6V14M6 10H14" stroke={customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id ? '#6750b8' : '#9CA3AF'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('ADD_NEW_ROW') || 'Add New Row'}</span>
          </Button>
        </div>
      )}
      </div>
    </div>
  );
};

export default ServicesTable;
